import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { API } from "../lib/api";
import { Image as ImageIcon, Loader2, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";
import { Link } from "react-router-dom";

/**
 * Quick-view spec gallery overlay for the dashboard. Lets users peek at the
 * uploaded spec images without leaving the table.
 */
export const SpecQuickViewDialog = ({ open, onOpenChange, record }) => {
  const [thumbs, setThumbs] = useState([]); // array of blob URLs aligned with record.spec_images
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !record) return;
    let cancelled = false;
    const created = [];
    setThumbs([]);
    const images = record.spec_images || [];
    if (images.length === 0) return;
    setLoading(true);
    const token = localStorage.getItem("wbt_token");
    Promise.all(
      images.map((_, i) =>
        fetch(`${API}/bath-records/${record.id}/spec-images/${i}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((r) => (r.ok ? r.blob() : null))
          .then((b) => {
            if (!b) return null;
            const url = URL.createObjectURL(b);
            created.push(url);
            return url;
          })
          .catch(() => null)
      )
    ).then((urls) => {
      if (!cancelled) setThumbs(urls);
      setLoading(false);
    });
    return () => {
      cancelled = true;
      created.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [open, record]);

  if (!record) return null;
  const images = record.spec_images || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="spec-quickview-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tighter flex items-center gap-2">
            <ImageIcon className="h-5 w-5" /> Spec — {record.supplier_po}
          </DialogTitle>
          <DialogDescription>
            {record.description} · {record.customer_ref || "no customer ref"}
          </DialogDescription>
        </DialogHeader>

        {images.length === 0 && (
          <div className="py-10 text-center text-sm text-zinc-500">
            <ImageIcon className="h-10 w-10 mx-auto mb-3 text-zinc-300" strokeWidth={1.5} />
            No spec uploaded for this record yet.
            <div className="mt-3">
              <Link to={`/bath/${record.id}`}>
                <Button variant="outline" data-testid="spec-quickview-open-record">
                  Open record to upload →
                </Button>
              </Link>
            </div>
          </div>
        )}

        {images.length > 0 && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[60vh] overflow-auto">
              {images.map((img, i) => (
                <div key={img.path} className="bg-zinc-50 border border-zinc-200 rounded-md overflow-hidden">
                  <div className="aspect-square bg-white flex items-center justify-center">
                    {thumbs[i] ? (
                      <a href={thumbs[i]} target="_blank" rel="noreferrer" data-testid={`spec-quickview-image-${i}`}>
                        <img src={thumbs[i]} alt={img.name} className="max-w-full max-h-full object-contain" />
                      </a>
                    ) : loading ? (
                      <Loader2 className="h-5 w-5 animate-spin text-zinc-300" />
                    ) : (
                      <span className="text-[11px] text-zinc-400">Couldn't load</span>
                    )}
                  </div>
                  <div className="px-2 py-1.5 text-[11px] flex justify-between items-center">
                    <span className="font-mono truncate text-zinc-900" title={img.name}>{img.name}</span>
                    <span className="text-zinc-400 ml-2 shrink-0">#{i + 1}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end">
              <Link to={`/bath/${record.id}`}>
                <Button variant="outline" size="sm" data-testid="spec-quickview-open">
                  <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Open record
                </Button>
              </Link>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
